import {
  RowDataValueType,
  JsonSchemaItems,
  ErrorState,
  RowDataReducerState,
  RowDataReducerAction,
} from "../../../types";
import {
  formatErrorMessage,
  hasDuplicateRows,
  noError,
  validateDateField,
  validateIfEvalMethodNeeded,
  validateNumber,
  validateUniqueCoRelationField,
  matchesPattern,
  formatPatternValidationMessage,
  checkItemsLengthIfNeeded,
} from "./validationUtils";
import { messages } from "../messages";
import { getIntl } from "../../../translate/i18n";

export const validateCoRelationRowDataInput = ({
  propertyKey,
  propertySubKey,
  rowIndex,
  newFieldValue,
  items,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: {
  propertyKey: string;
  propertySubKey: string;
  rowIndex: number;
  newFieldValue: RowDataValueType;
  items: JsonSchemaItems;
  selectedTabSectionKey: string | undefined;
  rowDataReducerState: RowDataReducerState;
  dispatch?: React.Dispatch<RowDataReducerAction>;
}): ErrorState => {
  const subProperty = getSubProperty(items, propertySubKey);
  if (!subProperty) return noError();

  if (isRequiredAndEmpty(items, propertySubKey, newFieldValue)) {
    return formatErrorMessage(getIntl().formatMessage(messages.required));
  }

  const uniqueFieldValidation = validateIfUniqueNeeded(
    subProperty.isUnique,
    propertyKey,
    propertySubKey,
    rowIndex,
    newFieldValue,
    selectedTabSectionKey,
    rowDataReducerState,
  );
  if (uniqueFieldValidation.error) return uniqueFieldValidation;

  const dateValidation = validateDateIfNeeded(
    subProperty.format,
    newFieldValue,
  );
  if (dateValidation.error) return dateValidation;

  const numberValidation = validateNumber(subProperty, Number(newFieldValue));
  if (numberValidation.error) return numberValidation;

  const patternValidation = validatePatternIfNeeded(
    subProperty.pattern,
    newFieldValue,
  );
  if (patternValidation.error) return patternValidation;

  const duplicateCheck = checkDuplicateIfNeeded(
    propertyKey,
    newFieldValue,
    selectedTabSectionKey,
    rowDataReducerState,
  );
  if (duplicateCheck.error) return duplicateCheck;

  const itemsLengthCheck = checkItemsLengthIfNeeded(subProperty, newFieldValue);
  if (itemsLengthCheck.error) return itemsLengthCheck;

  const customValidation = validateIfEvalMethodNeeded({
    evalMethod: subProperty.evalMethod,
    propertyKey,
    propertySubKey,
    rowIndex,
    newFieldValue,
    selectedTabSectionKey,
    rowDataReducerState,
    dispatch,
  });
  if (customValidation.error) return customValidation;

  return noError();
};

const getSubProperty = (items: JsonSchemaItems, propertySubKey: string) => {
  return items.type === "object" ? items.properties?.[propertySubKey] : undefined;
};

const isRequiredAndEmpty = (
  items: JsonSchemaItems,
  subKey: string,
  value: RowDataValueType,
): boolean => {
  const isEmpty = (value ?? "") === "";
  return (items.required ?? []).includes(subKey) && isEmpty;
};

const validateIfUniqueNeeded = (
  isUnique: boolean | undefined,
  propertyKey: string,
  propertySubKey: string,
  rowIndex: number,
  newFieldValue: RowDataValueType,
  selectedTabSectionKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  if (!isUnique) return noError();
  return validateUniqueCoRelationField(
    propertyKey,
    propertySubKey,
    rowIndex,
    newFieldValue,
    selectedTabSectionKey,
    rowDataReducerState,
  );
};

const validateDateIfNeeded = (
  format: string | undefined,
  newFieldValue: RowDataValueType,
): ErrorState => {
  if (!format) return noError();
  return validateDateField(format, newFieldValue as string);
};

const checkDuplicateIfNeeded = (
  propertyKey: string,
  value: RowDataValueType,
  selectedTabSectionKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  if ((value ?? "") === "") return noError();
  return hasDuplicateRows(
    propertyKey,
    selectedTabSectionKey,
    rowDataReducerState,
  );
};

const validatePatternIfNeeded = (
  pattern: string | undefined,
  value: RowDataValueType,
): ErrorState => {
  if (!pattern || !value) return noError();
  if (!matchesPattern(pattern, String(value))) {
    return formatErrorMessage(formatPatternValidationMessage(pattern));
  }
  return noError();
};
